import React, { useEffect, useRef, useMemo } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import { CashPaymentData } from '../../collectPayment.types';
import useCurrency from '../../../../../components/CurrencyProvider';

interface CashPaymentFormProps {
  data: CashPaymentData;
  onChange: (data: CashPaymentData) => void;
  error?: string;
  remainingAmount?: number;
}

const CashPaymentForm: React.FC<CashPaymentFormProps> = ({
  data,
  onChange,
  error,
  remainingAmount,
}) => {
  const { currencySymbol } = useCurrency();
  const prevRemainingRef = useRef(remainingAmount);
  const onChangeRef = useRef(onChange);

  // Keep refs updated with latest values
  onChangeRef.current = onChange;

  // Auto-populate amount with remaining amount when:
  // 1. Form is first rendered with no amount, OR
  // 2. Remaining amount changes (after adding a payment) and current amount is 0
  useEffect(() => {
    const remainingChanged = prevRemainingRef.current !== remainingAmount;
    prevRemainingRef.current = remainingAmount;
    
    if (remainingAmount && remainingAmount > 0 && !data.amount) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    } else if (remainingChanged && remainingAmount && remainingAmount > 0 && data.amount === 0) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    }
  }, [remainingAmount, data]);

  // Real-time validation: check if amount exceeds remaining
  const validationError = useMemo(() => {
    if (error) return error;
    if (remainingAmount !== undefined && data.amount > remainingAmount) {
      return 'Amount cannot exceed remaining balance';
    }
    return undefined;
  }, [error, data.amount, remainingAmount]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Amount"
          type="number"
          value={data.amount || ''}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            onChange({ ...data, amount: value });
          }}
          placeholder="0.00"
          error={!!validationError}
          helperText={validationError}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Notes"
          multiline
          minRows={2}
          maxRows={4}
          value={data.notes || ''}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          placeholder="Enter Note"
        />
      </Box>
    </Box>
  );
};

export default CashPaymentForm;
